/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Canonical AI önerisinden sahip ürünün mevcut formuna geçiş. Domain alanı veya writer içermez.
 */
import type { AssistantSuggestedAction } from "./AssistantRuntime";
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
const capabilityPattern = /^[a-z][a-z0-9._-]{3,119}$/;
const record = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));
const positiveId = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[1-9][0-9]{0,18}$/.test(value) &&
  BigInt(value) <= 9223372036854775807n;

export function readAssistantProductProposalReceipt(
  metadata: unknown,
): string | undefined {
  if (!record(metadata) || !record(metadata.actionReceipt)) return;
  const receipt = metadata.actionReceipt;
  return receipt.version === 1 &&
    receipt.type === "work" &&
    typeof receipt.id === "string" &&
    uuid.test(receipt.id)
    ? receipt.id
    : undefined;
}
/** The browser sends only canonical identity. AI re-reads the stored proposal; model payload is not authority. */
export function prepareAssistantProductForm(
  action: AssistantSuggestedAction,
  proposalReceiptId: unknown,
) {
  if (
    action.type !== "prepare_product_form" ||
    !record(action.payload) ||
    typeof action.payload.capability !== "string" ||
    !capabilityPattern.test(action.payload.capability) ||
    typeof proposalReceiptId !== "string" ||
    !uuid.test(proposalReceiptId)
  )
    return null;
  return { capability: action.payload.capability, proposalReceiptId };
}

/** root comes exclusively from deployment configuration/current origin; never from model or API data. */
export function buildAssistantProductFormUrl(
  value: unknown,
  companyId: string,
  capability: string,
  root: string,
): string | null {
  if (
    !record(value) ||
    value.contract !== "QP-PRODUCT-FORM-PREPARATION" ||
    value.version !== 1 ||
    value.state !== "PREPARED" ||
    value.documentCreated !== false ||
    value.capability !== capability ||
    !positiveId(companyId) ||
    value.companyId !== companyId ||
    typeof value.preparationId !== "string" ||
    !uuid.test(value.preparationId) ||
    typeof value.applicationCode !== "string" ||
    !/^[A-Z][A-Z0-9_-]{1,39}$/.test(value.applicationCode) ||
    typeof value.formPath !== "string" ||
    value.formPath.length > 240 ||
    !/^\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]+$/.test(value.formPath) ||
    !value.formPath.startsWith(`/${value.applicationCode.toLowerCase()}/`)
  )
    return null;
  try {
    const url = new URL(root);
    if (
      !["https:", "http:"].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash
    )
      return null;
    url.pathname = `${url.pathname.replace(/\/+$/, "")}${value.formPath}`;
    url.search = new URLSearchParams({
      preparationId: value.preparationId,
      companyId,
    }).toString();
    return url.toString();
  } catch {
    return null;
  }
}
export function getAssistantProductFormUrl(
  value: unknown,
  companyId: string,
  capability: string,
): string | null {
  const root =
    import.meta.env.VITE_BASE_URL ||
    import.meta.env.VITE_PLATFORM_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  return buildAssistantProductFormUrl(value, companyId, capability, root);
}
const COPY: Record<string, readonly [string, string, string]> = {
  tr: [
    "Formu incele",
    "Form hazırlanıyor…",
    "Form açılamadı. Aynı öneriyi yeniden deneyin.",
  ],
  en: [
    "Review form",
    "Preparing form…",
    "The form could not be opened. Retry the same suggestion.",
  ],
  ar: [
    "مراجعة النموذج",
    "جارٍ تحضير النموذج…",
    "تعذر فتح النموذج. أعد محاولة الاقتراح نفسه.",
  ],
  de: [
    "Formular prüfen",
    "Formular wird vorbereitet…",
    "Das Formular konnte nicht geöffnet werden. Versuchen Sie denselben Vorschlag erneut.",
  ],
  fr: [
    "Vérifier le formulaire",
    "Préparation du formulaire…",
    "Impossible d’ouvrir le formulaire. Réessayez la même proposition.",
  ],
  es: [
    "Revisar formulario",
    "Preparando formulario…",
    "No se pudo abrir el formulario. Reintente la misma propuesta.",
  ],
  it: [
    "Controlla modulo",
    "Preparazione del modulo…",
    "Impossibile aprire il modulo. Riprova la stessa proposta.",
  ],
  ru: [
    "Проверить форму",
    "Подготовка формы…",
    "Не удалось открыть форму. Повторите то же предложение.",
  ],
  zh: ["检查表单", "正在准备表单…", "无法打开表单。请重试同一建议。"],
  ja: [
    "フォームを確認",
    "フォームを準備中…",
    "フォームを開けませんでした。同じ提案で再試行してください。",
  ],
};
export function assistantProductFormCopy(language?: string) {
  const [open, pending, failed] =
    COPY[
      String(language || "tr")
        .toLowerCase()
        .split(/[-_]/)[0]
    ] ?? COPY.en;
  return { open, pending, failed };
}
